'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Alert, Skeleton } from '@mui/material';
import CustomCalendar from '../../components/custom_avail_calendar/availabilitlies_custom_calendar';
import AvailabilitiesBookingDetails from '../../components/availabilities_booking_details';
import { TimeRange } from '../../../../shared/types';
import { DayOfWeek, PricingRate, Venue, VenueBookedTimeSlot } from '../../../../shared/models';
import { getVenueOpenHours } from '../../lib/availability_calculator';
import { GetFullPrice } from '../../lib/price_calculator';
import InfoIcon from '@mui/icons-material/Info';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../lib/firebase/app';
import { useSearchParams } from 'next/navigation';
import { useAvailabilityStore } from '../../stores/availability_store';
import { trackEvent } from '../../lib/analytics';

const AvailabilitiesContent: React.FC<{ venue: Venue }> = ({ venue: initialVenue }) => {
    const {
        venue,
        selectedDate,
        selectedSection,
        numberOfPeople,
        openBarSelected,
        totalPrice,
        setSelectedDate,
        setSelectedSection,
        setNumberOfPeople,
        setOpenBarSelected,
        setTotalPrice,
        initializeVenue,
    } = useAvailabilityStore();

    // Initialize store with venue data when component mounts
    useEffect(() => {
        if (initialVenue && !venue) {
            initializeVenue(initialVenue);
        }
    }, [initialVenue, venue, initializeVenue]);

    const searchParams = useSearchParams();
    const dateParam = searchParams.get('date');
    const timeParam = searchParams.get('time');
    const sizeParam = searchParams.get('size');

    // Adjust the date parsing to handle timezone correctly
    const getAdjustedDate = (dateString: string) => {
        // Convert date string like "2024-12-07T05" to "2024-12-07T00:00"
        const formattedDate = dateString.split('T')[0] + 'T00:00';
        return new Date(formattedDate);
    };

    const [possibleTimeRangeForDay, setPossibleTimeRangeForDay] = useState<TimeRange[] | null>(() => {
        if (!dateParam || !venue) return null;  // Add venue check here

        const startDate = getAdjustedDate(dateParam);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
        endDate.setMilliseconds(endDate.getMilliseconds() - 1);

        const timeRanges = getVenueOpenHours(venue, startDate, endDate);
        return timeRanges.map(range => ({
            startTime: range.start,
            endTime: range.end,
            rangeId: range.start.getTime()
        }));
    });

    useEffect(() => {
        if (dateParam) {
            setSelectedDate(getAdjustedDate(dateParam));
        }
    }, [dateParam, setSelectedDate]);

    useEffect(() => {
        if (!venue?.Sections) return;

        if (sizeParam) {
            const size = parseInt(sizeParam);
            // Sort sections by capacity ascending and then by privacy level
            const sortedSections = Object.entries(venue.Sections)
                .sort((a, b) => {
                    // First sort by capacity ascending
                    const capacityDiff = a[1].Capacity - b[1].Capacity;
                    if (capacityDiff !== 0) return capacityDiff;

                    // Then sort by privacy level (non-private, semi-private, private)
                    const privacyOrder = {
                        'non-private': 0,
                        'semi-private': 1,
                        'private': 2
                    };

                    const aPrivacy = privacyOrder[a[1].PrivacyLevel || 'non-private'] || 0;
                    const bPrivacy = privacyOrder[b[1].PrivacyLevel || 'non-private'] || 0;

                    return aPrivacy - bPrivacy;
                });

            // Find smallest section that can fit the requested size
            const eligibleSection = sortedSections.find(([_, section]) => section.Capacity >= size);

            if (eligibleSection) {
                setSelectedSection(eligibleSection[0]);
            } else {
                // If no section can fit the size, return the section with maximum capacity
                const biggestSection = sortedSections[sortedSections.length - 1];
                setSelectedSection(biggestSection ? biggestSection[0] : '');
            }
        } else {
            // When no size parameter is provided, select the first section based on our sorting criteria
            const sortedSections = Object.entries(venue.Sections)
                .sort((a, b) => {
                    // First sort by capacity ascending
                    const capacityDiff = a[1].Capacity - b[1].Capacity;
                    if (capacityDiff !== 0) return capacityDiff;

                    // Then sort by privacy level (non-private, semi-private, private)
                    const privacyOrder = {
                        'non-private': 0,
                        'semi-private': 1,
                        'private': 2
                    };

                    const aPrivacy = privacyOrder[a[1].PrivacyLevel || 'non-private'] || 0;
                    const bPrivacy = privacyOrder[b[1].PrivacyLevel || 'non-private'] || 0;

                    return aPrivacy - bPrivacy;
                });

            setSelectedSection(sortedSections.length > 0 ? sortedSections[0][0] : '');
        }
    }, [sizeParam, venue, setSelectedSection]);

    useEffect(() => {
        if (!selectedSection || venue.Sections[selectedSection] === undefined) return;  // Don't proceed if no section is selected yet

        if (sizeParam) {
            const size = parseInt(sizeParam);
            setNumberOfPeople(Math.min(size, venue.Sections[selectedSection].Capacity));
        } else {
            setNumberOfPeople(Math.max(
                venue.PricingRatesPerSection[selectedSection]?.MinimumPeople || 1,
                Math.floor(venue.Sections[selectedSection].Capacity / 2)
            ));
        }
    }, [sizeParam, selectedSection, venue, setNumberOfPeople]);

    const [selectedTime, setSelectedTime] = useState<TimeRange | null>(() => {
        if (!timeParam || !dateParam || !venue) return null;

        // Create the requested date-time by combining date and time params
        const [hours, minutes] = timeParam.split(':').map(Number);
        const requestedDateTime = getAdjustedDate(dateParam);
        requestedDateTime.setHours(hours, minutes, 0, 0);

        const timeRanges = getVenueOpenHours(venue, requestedDateTime, requestedDateTime);

        // Find the slot that should contain the requested time
        const matchingSlot = timeRanges.find(slot => {
            return requestedDateTime >= slot.start && requestedDateTime <= slot.end;
        });

        if (!matchingSlot) {
            // If no exact match, find the next available slot
            const nextSlot = timeRanges.find(slot => slot.start >= requestedDateTime);
            if (nextSlot) {
                const minDuration = venue.PricingRatesPerSection[selectedSection]?.MinimumBookingDuration || 1;
                const endTime = new Date(nextSlot.start.getTime() + (minDuration * 60 * 60 * 1000));
                return {
                    startTime: nextSlot.start,
                    endTime: endTime,
                    rangeId: nextSlot.start.getTime()
                };
            }
            return null;
        }

        const minDuration = venue.PricingRatesPerSection[selectedSection]?.MinimumBookingDuration || 1;
        const startTime = new Date(requestedDateTime);
        startTime.setHours(hours, minutes, 0, 0);
        const endTime = new Date(startTime.getTime() + (minDuration * 60 * 60 * 1000));
        return {
            startTime: startTime,
            endTime: endTime,
            rangeId: matchingSlot.start.getTime()
        };
    });

    const [selectedSlot, setSelectedSlot] = useState<{ StartTime: Date; EndTime: Date } | null>(() => {
        if (!timeParam || !dateParam || !venue) return null;

        const requestedTime = new Date(`${dateParam?.split('T')[0]}T${timeParam}`);
        const timeRanges = getVenueOpenHours(venue, new Date(dateParam), new Date(dateParam));

        // Find first slot that contains the requested time
        const matchingSlot = timeRanges.find(slot => {
            return requestedTime >= slot.start && requestedTime <= slot.end;
        });

        if (!matchingSlot) return null;

        const minDuration = venue.PricingRatesPerSection[selectedSection]?.MinimumBookingDuration || 1;
        const endTime = new Date(matchingSlot.start.getTime() + (minDuration * 60 * 60 * 1000));

        // Make sure end time doesn't exceed slot end time
        const slotEndTime = endTime > matchingSlot.end ? matchingSlot.end : endTime;

        return {
            StartTime: matchingSlot.start,
            EndTime: slotEndTime
        };
    });

    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [bookedTimeSlotsByDay, setBookedTimeSlotsByDay] = useState<Record<string, VenueBookedTimeSlot[]>>({});
    const [isMobile, setIsMobile] = useState<boolean>(false);
    const [showFutureAvailabilityAlert, setShowFutureAvailabilityAlert] = useState<boolean>(false);
    const [pricingViewTime, setPricingViewTime] = useState<Date | null>(null);

    // Track when user views pricing details
    useEffect(() => {
        if (totalPrice !== null && selectedTime) {
            setPricingViewTime(new Date());
            trackEvent('venue_pricing_viewed', {
                venue_id: venue?.ID,
                venue_name: venue?.Name,
                section: selectedSection,
                price: totalPrice,
                date: selectedTime?.startTime?.toISOString(),
                time_range: `${selectedTime?.startTime?.toLocaleTimeString()} - ${selectedTime?.endTime?.toLocaleTimeString()}`,
                people: numberOfPeople,
                is_mobile: window.innerWidth <= 768
            });
        }
    }, [totalPrice, selectedTime, venue?.ID, venue?.Name, selectedSection, numberOfPeople]);

    // Track when user exits after viewing pricing
    useEffect(() => {
        return () => {
            if (pricingViewTime && totalPrice !== null) {
                const timeSpentMs = new Date().getTime() - pricingViewTime.getTime();
                trackEvent('venue_pricing_abandoned', {
                    venue_id: venue?.ID,
                    venue_name: venue?.Name,
                    section: selectedSection,
                    price: totalPrice,
                    time_spent_seconds: Math.round(timeSpentMs / 1000),
                    is_mobile: window.innerWidth <= 768
                });
            }
        };
    }, [pricingViewTime, totalPrice, venue?.ID, venue?.Name, selectedSection]);

    // Reset pricing view time when user navigates back to calendar
    const handleBackToCalendar = () => {
        if (pricingViewTime && totalPrice !== null) {
            const timeSpentMs = new Date().getTime() - pricingViewTime.getTime();
            trackEvent('venue_pricing_abandoned', {
                venue_id: venue?.ID,
                venue_name: venue?.Name,
                section: selectedSection,
                price: totalPrice,
                time_spent_seconds: Math.round(timeSpentMs / 1000),
                is_mobile: window.innerWidth <= 768,
                action: 'back_to_calendar'
            });
        }
        setPricingViewTime(null);
        setSelectedTime(null);
        setPossibleTimeRangeForDay(null);
        setTotalPrice(null);
    };

    const handleDateChange = (date: Date, selectedSection: string, bookedTimeSlots: Record<string, VenueBookedTimeSlot[]>) => {
        setSelectedDate(date);
        setSelectedSection(selectedSection);

        // Get venue open hours for the selected date
        const startDate = new Date(date);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 1);
        endDate.setMilliseconds(endDate.getMilliseconds() - 1);

        const timeRanges = getVenueOpenHours(venue, startDate, endDate);
        if (timeRanges.length > 0) {
            const timeRangeForDay = timeRanges.map(range => ({
                startTime: range.start,
                endTime: range.end
            }));
            setPossibleTimeRangeForDay(timeRangeForDay as TimeRange[]);
            setBookedTimeSlotsByDay(bookedTimeSlots);
        } else {
            setPossibleTimeRangeForDay(null);
        }
    };

    const handleOkClick = () => {
        if (
            selectedTime &&
            totalPrice !== null &&
            selectedSection
        ) {
            const startTime = selectedTime.startTime.toISOString();
            const endTime = selectedTime.endTime.toISOString();
            const price = totalPrice;
            const queryParams = new URLSearchParams({
                startTime,
                endTime,
                venueId: venue.ID,
                section: selectedSection,
                people: numberOfPeople.toString(),
                openBar: openBarSelected.toString(),
            }).toString();
            logEvent(analytics, 'checkout_initiated', {
                currency: 'USD',
                value: price,
                items: [{
                    item_id: venue.ID,
                    item_name: venue.Name,
                    item_category: 'Venue',
                    item_variant: selectedSection,
                    price: price,
                    quantity: 1
                }],
                // Custom parameters
                start_time: startTime,
                end_time: endTime,
                number_of_people: numberOfPeople,
                open_bar: openBarSelected,
            });
            window.location.href = `/checkout?${queryParams}`;
        }
    };

    useEffect(() => {
        if (!selectedTime) {
            setTotalPrice(null);
            return;
        }

        // Get the start and end times
        const startTime = selectedTime.startTime;
        const endTime = selectedTime.endTime;

        // Check if end time is before start time (invalid case)
        if (endTime <= startTime) {
            setTotalPrice(null);
            return;
        }

        // Normal case: calculate hours when end time is after start time
        const totalHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
        const minimumBookingHours = venue.PricingRatesPerSection[selectedSection]?.MinimumBookingDuration || 0;

        if (totalHours < minimumBookingHours) {
            setTotalPrice(null);
            return;
        }

        // Check if the selected section is already booked for any time inside the slot
        const isSlotAvailable = (() => {
            // Check if the selected section overlaps all other sections
            if (venue.Sections[selectedSection].OverlapsAllOtherSections) {
                // If it does, check all sections for any booking during the slot time
                for (const sectionId in bookedTimeSlotsByDay) {
                    const sectionBookedSlots = bookedTimeSlotsByDay[sectionId][selectedTime.startTime.toDateString()] || [];
                    if (sectionBookedSlots.some(bookedSlot =>
                        (selectedTime.startTime < new Date(bookedSlot.EndTime) && new Date(bookedSlot.StartTime) < selectedTime.endTime)
                    )) {
                        setErrorMessage(`The selected time conflicts with a booking in ${venue.Sections[sectionId].Name}.`);
                        return false;
                    }
                }
            } else {
                // If it doesn't, check the selected section and any section that overlaps all others
                const sectionBookedSlots = bookedTimeSlotsByDay[selectedSection]?.[selectedTime.startTime.toDateString()] || [];
                if (sectionBookedSlots.some(bookedSlot =>
                    (selectedTime.startTime <= new Date(bookedSlot.EndTime) && new Date(bookedSlot.StartTime) <= selectedTime.endTime)
                )) {
                    setErrorMessage(`The selected time is already booked in ${venue.Sections[selectedSection].Name}.`);
                    return false;
                }

                for (const sectionId in venue.Sections) {
                    if (venue.Sections[sectionId].OverlapsAllOtherSections) {
                        const overlappingSectionSlots = bookedTimeSlotsByDay[sectionId]?.[selectedTime.startTime.toDateString()] || [];
                        if (overlappingSectionSlots.some(bookedSlot =>
                            (selectedTime.startTime < new Date(bookedSlot.EndTime) && new Date(bookedSlot.StartTime) < selectedTime.endTime)
                        )) {
                            setErrorMessage(`The selected time conflicts with a booking in ${venue.Sections[sectionId].Name}, which overlaps all sections.`);
                            return false;
                        }
                    }
                }
            }
            return true;
        })();

        if (!isSlotAvailable) {
            setTotalPrice(null);
            return;
        }

        // Clear error message if slot is available
        setErrorMessage('');

        const dayOfWeek = selectedTime.startTime.toLocaleString('en-US', { weekday: 'long' }) as DayOfWeek;
        let pricingRate = venue.PricingRatesPerSectionPerDayOfWeek?.[selectedSection]?.[dayOfWeek]
            || venue.PricingRatesPerSection[selectedSection]
            || null;

        let overriddenPriceRange = venue.PricingRatesPerSectionOverride?.[selectedSection]?.find(override => {
            return selectedTime.startTime >= override.StartTime && selectedTime.endTime <= override.EndTime;
        });

        if (overriddenPriceRange) {
            pricingRate = overriddenPriceRange.PricingRate;
        }

        if (!pricingRate) {
            pricingRate = venue.PricingRatesPerSection[selectedSection];
        }

        const newTotalPrice = GetFullPrice(
            selectedTime.startTime,
            selectedTime.endTime,
            pricingRate,
            numberOfPeople,
            openBarSelected
        );

        setTotalPrice(newTotalPrice.toFixed(2));
    }, [venue, selectedTime, bookedTimeSlotsByDay, numberOfPeople, openBarSelected]);

    const handleTimeSelect = (time: TimeRange, section: string, price: string) => {
        // Check if the selected time range overlaps with multiple venue availability time ranges
        const dayOfWeek = time.startTime.toLocaleString('en-US', { weekday: 'long' }) as DayOfWeek;
        const dayAvailability = venue.Availability[dayOfWeek];

        if (dayAvailability?.Times) {
            let overlappingRanges = 0;
            dayAvailability.Times.forEach(availableTime => {
                if (time.startTime < availableTime.EndTime && availableTime.StartTime < time.endTime) {
                    overlappingRanges++;
                }
            });

            if (overlappingRanges > 1) {
                setErrorMessage('Selected time range overlaps with multiple available time slots');
                return;
            }
        }

        setSelectedSlot({ StartTime: time.startTime, EndTime: time.endTime });
        setSelectedTime(time);
        setSelectedSection(section);
        setTotalPrice(price);
    };


    // Add loading state
    if (!venue || !venue.Sections || Object.keys(venue.Sections).length === 0) {
        return <Skeleton variant="rectangular" height={500} />;
    }


    return (
        <Box
            sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: 2,
                borderRadius: 2,
                marginTop: 1,
                marginBottom: 1,
                width: '100%',
                boxShadow: '4px 4px 8px rgba(0, 0, 0, 0.2)',
            }}
        >
            <Box
                component="section"
                aria-label="availability-controls"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: isMobile ? 1 : 2,
                }}
            >
                <Typography
                    fontFamily='Inter'
                    fontSize={isMobile ? '1.5em' : { xs: '1em', md: '2em' }}
                    sx={{
                        color: '#EEEEEE',
                        fontWeight: 'bold',
                    }}
                >
                    {possibleTimeRangeForDay ? 'Booking Details' : 'Instant Book'}

                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {possibleTimeRangeForDay && (
                        <Typography
                            fontFamily='Inter'
                            fontSize={isMobile ? '0.9em' : '1em'}
                            sx={{
                                color: '#EEEEEE',
                                opacity: 0.9,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <span>{selectedDate?.toLocaleDateString('en-US', {
                                weekday: 'short',
                                month: 'short',
                                day: 'numeric'
                            })}</span>
                        </Typography>
                    )}
                    {possibleTimeRangeForDay && (
                        <Button
                            onClick={handleBackToCalendar}
                            sx={{
                                color: 'white',
                                fontFamily: 'Inter',
                                fontSize: isMobile ? '0.9em' : { xs: '0.6em' },
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                },
                            }}
                        >
                            Change Date
                        </Button>
                    )}
                </Box>
            </Box>
            {showFutureAvailabilityAlert && (
                <Alert severity='info' sx={{ mt: 1, mb: 1, py: isMobile ? 0.5 : 1 }}>
                    <Typography fontFamily='Inter' fontSize={isMobile ? '0.9em' : '1em'}>
                        These are the first available dates for this venue.
                    </Typography>
                </Alert>
            )}
            {!!venue?.PricingRatesPerSection[selectedSection]?.TowardsMinimumSpend && possibleTimeRangeForDay && (
                <Box
                    sx={{
                        mt: 1,
                        mb: 1,
                        p: 1,
                        pr: 3,
                        backgroundColor: '#F0F7FF',
                        border: '1px solid #90CDF4',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <InfoIcon sx={{ color: '#3182CE' }} />
                    <Typography
                        fontFamily='Inter'
                        fontSize={isMobile ? '0.9em' : '1em'}
                        color='#2C5282'
                        fontWeight={500}
                        lineHeight={1.5}
                    >
                        The displayed price represents a minimum spend requirement that can be used towards food and drinks at the venue.
                    </Typography>
                </Box>
            )}
            {/* Combined notification box for no minimum spend option OR deposit percentage */}
            {(!!venue?.PricingRatesPerSection[selectedSection]?.IsNoMinimumSpendOption ||
                venue?.PricingRatesPerSection[selectedSection]?.DepositPercentage > 0) &&
                possibleTimeRangeForDay && (
                    <Box
                        sx={{
                            mt: 2,
                            mb: 3,
                            p: 2.5,
                            backgroundColor: '#f9f5ff',
                            border: '1px solid #e6d8ff',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}
                    >
                        <InfoIcon sx={{ color: '#9061F9', fontSize: '1.2rem' }} />
                        <Box>
                            <Typography
                                fontFamily='Inter'
                                fontSize={isMobile ? '1rem' : '1.05rem'}
                                color='#4A2D91'
                                fontWeight={600}
                                lineHeight={1.3}
                                mb={0.5}
                            >
                                {!!venue?.PricingRatesPerSection[selectedSection]?.IsNoMinimumSpendOption
                                    ? 'Good news! This reservation is free!'
                                    : 'Good news! This reservation only requires a deposit!'}
                            </Typography>
                            <Typography
                                fontFamily='Inter'
                                fontSize={isMobile ? '0.85rem' : '0.9rem'}
                                color='#6A4FB5'
                                lineHeight={1.4}
                            >
                                Your deposit will be automatically refunded after you check in at the venue
                            </Typography>
                        </Box>
                    </Box>
                )}

            {/* Subtle upgrade suggestion - only for free reservations and non-private sections */}
            {!!venue?.PricingRatesPerSection[selectedSection]?.IsNoMinimumSpendOption &&
                possibleTimeRangeForDay &&
                venue.Sections[selectedSection]?.PrivacyLevel !== 'private' &&
                Object.entries(venue.Sections).some(([id, section]) =>
                    section.PrivacyLevel === (venue.Sections[selectedSection]?.PrivacyLevel === 'semi-private' ? 'private' : 'semi-private')
                ) && (
                    <Box
                        sx={{
                            mt: 1,
                            mb: 2,
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px',
                            overflow: 'hidden'
                        }}
                    >
                        {(() => {
                            const targetPrivacyLevel = venue.Sections[selectedSection]?.PrivacyLevel === 'semi-private'
                                ? 'private'
                                : 'semi-private';

                            const upgradeSections = Object.entries(venue.Sections)
                                .filter(([id, section]) =>
                                    section.PrivacyLevel === targetPrivacyLevel &&
                                    section.Capacity >= numberOfPeople
                                )
                                .map(([id, section]) => ({
                                    id,
                                    section,
                                    rate: venue.PricingRatesPerSection[id] || null
                                }))
                                .filter(item => item.rate);

                            if (upgradeSections.length === 0) return null;

                            // Sort by total price
                            upgradeSections.sort((a, b) => {
                                const aRate = a.rate as PricingRate;
                                const bRate = b.rate as PricingRate;
                                const aPrice = (aRate.RatePerHour || 0) + (aRate.RatePerPerson || 0) * numberOfPeople;
                                const bPrice = (bRate.RatePerHour || 0) + (bRate.RatePerPerson || 0) * numberOfPeople;
                                return aPrice - bPrice;
                            });

                            const cheapestUpgrade = upgradeSections[0];
                            if (!cheapestUpgrade) return null;

                            const currentRate = venue.PricingRatesPerSection[selectedSection];
                            const upgradeRate = cheapestUpgrade.rate;

                            // Calculate price difference
                            let priceDiffPerPerson = 5; // Minimum price difference

                            if (selectedTime && selectedTime.startTime && selectedTime.endTime) {
                                const currentSectionPrice = GetFullPrice(
                                    selectedTime.startTime,
                                    selectedTime.endTime,
                                    currentRate as PricingRate,
                                    numberOfPeople,
                                    openBarSelected
                                );
                                const upgradeSectionPrice = GetFullPrice(
                                    selectedTime.startTime,
                                    selectedTime.endTime,
                                    upgradeRate as PricingRate,
                                    numberOfPeople,
                                    openBarSelected
                                );
                                const totalPriceDiff = upgradeSectionPrice - currentSectionPrice;
                                priceDiffPerPerson = Math.max(5, Math.ceil(totalPriceDiff / numberOfPeople));
                            } else {
                                const currentPerPerson = currentRate?.RatePerPerson || 0;
                                const upgradePerPerson = upgradeRate?.RatePerPerson || 0;
                                priceDiffPerPerson = Math.max(5, Math.ceil(upgradePerPerson - currentPerPerson));
                            }

                            return (
                                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography
                                            fontFamily='Inter'
                                            fontSize={isMobile ? '0.85rem' : '0.9rem'}
                                            color="#555"
                                            fontWeight={500}
                                        >
                                            Want more privacy? Get the {cheapestUpgrade.section.Name}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setSelectedSection(cheapestUpgrade.id)}
                                        size="small"
                                        sx={{
                                            color: '#8e44ad',
                                            borderColor: '#8e44ad',
                                            fontFamily: 'Inter',
                                            textTransform: 'none',
                                            fontWeight: '500',
                                            ml: 1,
                                            fontSize: '0.8rem',
                                            '&:hover': {
                                                borderColor: '#7d32a8',
                                                backgroundColor: 'rgba(142, 68, 173, 0.04)',
                                            }
                                        }}
                                    >
                                        +${priceDiffPerPerson}/person
                                    </Button>
                                </Box>
                            );
                        })()}
                    </Box>
                )}

            {venue ? (
                possibleTimeRangeForDay ? (
                    <AvailabilitiesBookingDetails
                        possibleTimeRangeForDay={possibleTimeRangeForDay}
                        isNoMinimumSpendRequired={!!venue?.PricingRatesPerSection[selectedSection]?.IsNoMinimumSpendOption}
                        handleOkClick={handleOkClick}
                        errorMessage={errorMessage}
                        bookedTimeSlotsByDay={bookedTimeSlotsByDay}
                        selectedSlot={selectedSlot}
                        selectedStartTime={selectedTime?.startTime}
                        handleSelectedTimeChange={handleTimeSelect}
                    />
                ) : (
                    <CustomCalendar
                        venue={venue}
                        selectedDate={selectedDate || new Date()}
                        selectedSection={selectedSection}
                        onDateChange={handleDateChange}
                        onSectionChange={setSelectedSection}
                        minDate={new Date()}
                    />
                )
            ) : (
                <Box sx={{
                    width: {
                        xs: '100vw',
                        sm: '400px'
                    },
                    height: {
                        xs: '100vh',
                        sm: '600px'
                    }
                }}>
                    <Skeleton
                        variant='rectangular'
                        width='100%'
                        height='100%'
                        sx={{
                            borderRadius: {
                                xs: 0,
                                sm: 2
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

export default AvailabilitiesContent;
